import { useMemo, useState } from "react";
import { Flame, RotateCcw, Thermometer } from "@/lib/icons";
import { Button } from "@/components/ui/button";

/**
 * Coffee-cup calorimetry simulator.
 *
 * Model: a known mass of solid (or salt) at temperature T_solid is dropped
 * into a known mass of water at T_water. Heat lost by the hotter substance
 * equals heat gained by the cooler one, ignoring calorimeter losses:
 *
 *   m_s * c_s * (T_f - T_solid) + m_w * c_w * (T_f - T_water) = 0
 *
 * Solving for T_f gives the equilibrium temperature.
 */

const SAMPLES = [
  { id: "cu", label: "Copper (c = 0.385 J/g·°C)", c: 0.385, defaultT: 95, defaultMass: 50 },
  { id: "al", label: "Aluminum (c = 0.897 J/g·°C)", c: 0.897, defaultT: 95, defaultMass: 50 },
  { id: "fe", label: "Iron (c = 0.449 J/g·°C)", c: 0.449, defaultT: 95, defaultMass: 50 },
  { id: "pb", label: "Lead (c = 0.128 J/g·°C)", c: 0.128, defaultT: 95, defaultMass: 50 },
] as const;

const C_WATER = 4.184; // J/g·°C

export default function Calorimetry() {
  const [sampleIdx, setSampleIdx] = useState(0);
  const sample = SAMPLES[sampleIdx];
  const [mSolid, setMSolid] = useState<number>(sample.defaultMass);
  const [tSolid, setTSolid] = useState<number>(sample.defaultT);
  const [mWater, setMWater] = useState(100);
  const [tWater, setTWater] = useState(22);
  const [run, setRun] = useState(false);

  const tFinal = useMemo(() => {
    const num = sample.c * mSolid * tSolid + C_WATER * mWater * tWater;
    const den = sample.c * mSolid + C_WATER * mWater;
    return num / den;
  }, [sample, mSolid, tSolid, mWater, tWater]);

  const qSolid = sample.c * mSolid * (tFinal - tSolid); // negative (heat lost)
  const qWater = C_WATER * mWater * (tFinal - tWater); // positive (heat gained)

  const reset = () => {
    setRun(false);
    setSampleIdx(0);
    setMSolid(SAMPLES[0].defaultMass as number);
    setTSolid(SAMPLES[0].defaultT as number);
    setMWater(100);
    setTWater(22);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
      {/* Visualization */}
      <div className="rounded-lg border border-border bg-tile-bg p-6 flex flex-col items-center justify-center min-h-[360px]">
        <div className="relative w-48 h-48">
          {/* Coffee cup */}
          <div className="absolute inset-0 rounded-b-3xl rounded-t-md border-4 border-foreground/30 bg-gradient-to-b from-transparent to-primary/10 overflow-hidden">
            <div
              className="absolute bottom-0 left-0 right-0 transition-all duration-700"
              style={{
                height: `${Math.min(100, (mWater / 200) * 100)}%`,
                background: `linear-gradient(to top, hsl(210, ${30 + Math.min(60, tFinal)}%, ${60 - Math.min(20, tFinal / 5)}%), hsl(210, 50%, 80%))`,
              }}
            />
            {/* Solid block once dropped */}
            {run && (
              <div
                className="absolute left-1/2 -translate-x-1/2 bottom-3 w-10 h-10 rounded bg-foreground/40 border border-foreground/60"
                aria-hidden="true"
              />
            )}
          </div>
        </div>
        <div className="mt-6 grid grid-cols-3 gap-4 w-full max-w-md text-center">
          <Stat
            label="T solid"
            value={`${tSolid.toFixed(1)} °C`}
            tone="warn"
          />
          <Stat
            label="T water (start)"
            value={`${tWater.toFixed(1)} °C`}
          />
          <Stat
            label="T equilibrium"
            value={run ? `${tFinal.toFixed(2)} °C` : "-"}
            tone="primary"
          />
        </div>
      </div>

      {/* Controls */}
      <div className="space-y-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Sample
          </label>
          <select
            value={sampleIdx}
            onChange={(e) => setSampleIdx(Number(e.target.value))}
            className="mt-1 w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            {SAMPLES.map((s, i) => (
              <option key={s.id} value={i}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <Slider label="Sample mass (g)" value={mSolid} min={10} max={200} step={5} onChange={setMSolid} />
        <Slider label="Sample T (°C)" value={tSolid} min={25} max={200} step={1} onChange={setTSolid} />
        <Slider label="Water mass (g)" value={mWater} min={25} max={200} step={5} onChange={setMWater} />
        <Slider label="Water T (°C)" value={tWater} min={5} max={40} step={1} onChange={setTWater} />

        <div className="flex gap-2">
          <Button size="sm" onClick={() => setRun(true)} className="gap-1.5 flex-1">
            <Flame size={14} aria-hidden="true" />
            Drop sample in
          </Button>
          <Button size="sm" variant="outline" onClick={reset} className="gap-1.5">
            <RotateCcw size={14} aria-hidden="true" />
            Reset
          </Button>
        </div>

        {run && (
          <div className="rounded-md border border-border bg-muted/30 p-3 space-y-1 text-xs font-mono">
            <p className="text-muted-foreground">Heat balance:</p>
            <p>q_sample = m·c·ΔT = {qSolid.toFixed(0)} J</p>
            <p>q_water  = m·c·ΔT = {qWater.toFixed(0)} J</p>
            <p className="text-muted-foreground">
              |q_sample| ≈ |q_water| (energy conserved, no calorimeter loss).
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "warn" | "primary";
}) {
  const color =
    tone === "primary"
      ? "text-primary"
      : tone === "warn"
      ? "text-amber-500"
      : "text-foreground";
  return (
    <div className="rounded-md border border-border bg-background py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center justify-center gap-1">
        <Thermometer size={10} aria-hidden="true" /> {label}
      </div>
      <div className={`font-mono text-sm font-semibold ${color}`}>{value}</div>
    </div>
  );
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <label className="font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </label>
        <span className="font-mono text-foreground">{value}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full mt-1 accent-primary"
      />
    </div>
  );
}